import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink, Mic, Copy, Check } from 'lucide-react';
import { fetchRespondentByPhone, fetchSessionDetail } from '../../api/survey.js';
import { SourceChip } from './SourceChip.jsx';
import { InlineAudioPlayer } from './InlineAudioPlayer.jsx';

/**
 * SurveySessionTimeline — 該 phone / key 的所有 session 時間軸
 *
 * 仿 CustomerTimeline，每筆 session 一張卡：
 *   時間 / source chip / campaign / 答題 X/Y / 時長 / 錄音 / →detail
 */
export function SurveySessionTimeline({ avatarId, hostId, phone, groupKey, campaigns = [], onOpenSession }) {
    const { t } = useTranslation('survey');
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const r = await fetchRespondentByPhone(avatarId, {
                    phone, key: groupKey, hostId, includeFake: true,
                });
                if (!cancelled) setSessions(r.sessions || []);
            } catch (e) {
                if (!cancelled) setError(e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [avatarId, hostId, phone, groupKey]);

    if (loading) return <div className="text-xs text-ink-400 italic">{t('timeline.loading')}</div>;
    if (error) return <div className="text-xs text-danger-500">{error.message}</div>;
    if (!sessions.length) return <div className="text-xs text-ink-400 italic">{t('timeline.empty')}</div>;

    const campMap = new Map(campaigns.map((c) => [c.id, c.name]));

    return (
        <div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-ink-400 mb-3">
                {t('timeline.title', { n: sessions.length })}
            </div>
            <div className="space-y-2">
                {sessions.map((s) => {
                    const ts = s.startedAt ? new Date(s.startedAt) : null;
                    const campaignName = s.campaignId ? campMap.get(s.campaignId) : null;
                    return (
                        <div
                            key={s.sessionId}
                            className="bg-white border border-cream-200 rounded-md px-4 py-3 hover:border-accent-300 transition"
                        >
                            <div className="flex items-center gap-4 cursor-pointer"
                                onClick={() => onOpenSession && onOpenSession(s.sessionId)}>
                                <div className="shrink-0 w-28 tnum">
                                    <div className="text-sm text-ink-900">
                                        {ts ? ts.toLocaleDateString('zh-TW', { month: '2-digit', day: '2-digit' }) : '—'}
                                    </div>
                                    <div className="mono-caption">
                                        {ts ? ts.toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' }) : ''}
                                    </div>
                                </div>
                                <div className="shrink-0">
                                    <SourceChip source={s.source} campaignName={campaignName} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm text-ink-900">
                                        {t('timeline.answered')} <span className="tnum font-medium">{s.answeredCount}/{s.totalQuestions}</span>
                                        {s.completed && <span className="ml-2 text-emerald-600 text-xs">{t('completedMark')}</span>}
                                    </div>
                                    <div className="mono-caption mt-0.5">
                                        {t('duration.label')} {formatDuration(s.durationSec, t)}
                                        {s.recordingUrl && (
                                            <span className="ml-2 inline-flex items-center gap-1 text-accent-700">
                                                <Mic size={11} /> {t('timeline.hasRecording')}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <ExternalLink size={14} className="text-ink-400 shrink-0" />
                            </div>
                            {/* Session ID + 內嵌錄音播放器 */}
                            <div className="mt-2 pt-2 border-t border-cream-100 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                <SessionIdChip sessionId={s.sessionId} />
                            </div>
                            {s.recordingUrl && (
                                <div className="mt-2" onClick={(e) => e.stopPropagation()}>
                                    <InlineAudioPlayer
                                        src={s.recordingUrl}
                                        refresh={() => fetchSessionDetail(avatarId, s.sessionId, { hostId }).then((r) => r?.recordingUrl || null)}
                                    />
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function SessionIdChip({ sessionId }) {
    const { t } = useTranslation('survey');
    const [copied, setCopied] = useState(false);
    if (!sessionId) return null;
    const copy = () => {
        navigator.clipboard?.writeText(sessionId).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        }).catch(() => {});
    };
    return (
        <button onClick={copy} title={t('timeline.copySessionId', { id: sessionId })}
            className="inline-flex items-center gap-1.5 font-mono text-[10px] text-ink-400 hover:text-ink-700 transition group">
            <span className="text-ink-300">session</span>
            <span className="truncate max-w-[220px]">{sessionId}</span>
            {copied ? <Check size={10} className="text-emerald-600" /> : <Copy size={10} className="opacity-0 group-hover:opacity-100" />}
        </button>
    );
}

function formatDuration(sec, t) {
    if (sec == null || sec < 0) return '—';
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return t('duration.value', { m, s: String(s).padStart(2, '0') });
}
